import React, { useMemo, useState } from 'react';
import type { AiModelConfig, AiModelOption, AiPromptTemplate, AiTestProject } from './types';
import { createTesterRun } from './api';
import ConceptTargetPicker, { type ConceptTargetSelection } from './ConceptTargetPicker';
import ModelPicker from './ModelPicker';
import PromptPicker from './PromptPicker';

interface VisualConceptFormProps {
  mode: 'generate' | 'regenerate';
  projects: AiTestProject[];
  models: AiModelOption[];
  prompts: AiPromptTemplate[];
  onPromptsChanged: () => Promise<void> | void;
  onRunStarted: () => Promise<void> | void;
}

const VisualConceptForm: React.FC<VisualConceptFormProps> = ({
  mode,
  projects,
  models,
  prompts,
  onPromptsChanged,
  onRunStarted,
}) => {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [target, setTarget] = useState<ConceptTargetSelection | null>(null);
  const [modelId, setModelId] = useState('');
  const [modelConfig, setModelConfig] = useState<AiModelConfig>({});
  const [prompt, setPrompt] = useState<AiPromptTemplate | null>(null);
  const [promptContent, setPromptContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const generatorKey = mode === 'regenerate' ? 'visual_concept_regenerate' : 'visual_concept';
  const visualPrompts = useMemo(
    () => prompts.filter((p) => !p.step_key || p.step_key === ''),
    [prompts]
  );
  const complete = Boolean(target?.combination.attributes_complete);
  const hasWritten = Boolean((target?.artifact?.written_concept || '').trim());
  const canRun = Boolean(
    project
    && target
    && complete
    && target.artifact
    && hasWritten
  );

  const handleRun = async () => {
    if (!project || !target?.artifact) {
      setError('Select a project and a written concept first.');
      return;
    }
    if (!target.combination.attributes_complete) {
      setError(
        `Select an option for every attribute first. Missing: ${
          target.combination.missing_attributes.join(', ') || 'attributes'
        }.`
      );
      return;
    }
    if (!hasWritten) {
      setError('This concept has no written text yet.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const inline = promptContent.trim();
    try {
      const run = await createTesterRun({
        project_id: project.id,
        generator_key: generatorKey,
        blueprint_id: target.blueprint.id,
        tab_id: target.tab.id,
        combination_id: target.combination.id,
        artifact_id: target.artifact.id,
        model_id: modelId || undefined,
        model_config: modelConfig,
        prompt_inline: inline || undefined,
        prompt_id: inline ? undefined : prompt?.id || undefined,
        blob_filename: inline || prompt?.id ? undefined : prompt?.blob_filename || undefined,
      });
      setSuccess(`Run #${run.id} started (${run.status}).`);
      await onRunStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value ? Number(e.target.value) : '');
            setTarget(null);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        >
          <option value="">Select a project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <ConceptTargetPicker
        projectId={projectId}
        value={target}
        onChange={setTarget}
        requireArtifact
        artifactLabel="Concept tab"
        disabled={submitting}
      />

      <PromptPicker
        label="Prompt override (optional)"
        generatorKey={generatorKey}
        stepKey=""
        prompts={visualPrompts}
        selectedId={prompt?.id ?? null}
        blobFilename={prompt?.blob_filename || ''}
        onSelect={setPrompt}
        onContentChange={setPromptContent}
        onPromptsChanged={onPromptsChanged}
        disabled={submitting}
      />

      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey={generatorKey}
        disabled={submitting}
      />

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={submitting || !canRun}
        className="h-9 px-4 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting
          ? 'Starting…'
          : mode === 'regenerate'
            ? 'Run Visual Regenerate'
            : 'Run Visual Concept'}
      </button>
    </div>
  );
};

export default VisualConceptForm;
